import { useCallback, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { CONFIG_FIELD_TYPE, IConfigClass } from '../../../../../constants/configurations.constants';
import {
  NudosStaticAlertInComponent,
  NudosTextArea,
  NudosTextInput,
  NudosUploadDownloadButton
} from '../../../../NudosComponents';

import './ConfigFieldsForm.scss';

export interface IConfigFieldsFormProps {
  selectedConfigClass: IConfigClass;
  isDisabled?: boolean;
  formValues: Record<number, string | File>;
  setFormValues?: Dispatch<SetStateAction<Record<number, string | File>>>;
  setIsFormValid?: Dispatch<SetStateAction<boolean>>;
  showAlert?: boolean;
}

const ConfigFieldsForm = ({
  selectedConfigClass,
  formValues,
  isDisabled = false,
  setFormValues,
  setIsFormValid,
  showAlert = true
}: IConfigFieldsFormProps) => {
  const { fields, gridPositions, alert } = selectedConfigClass;
  const isInitialMount = useRef(true);
  const lastInitialValues = useRef(formValues);

  const validateRequiredFields = useCallback(
    (values: Record<number, string | File>) => {
      if (!fields) return true;

      return fields.every(field => {
        if (field.isRequired) {
          const value = values[field.id];
          const isEmpty = !value || (typeof value === 'string' && value.trim() === '');
          return !isEmpty;
        }
        return true;
      });
    },
    [fields]
  );

  const handleChange = useCallback(
    (fieldId: number, value: string | File) => {
      if (!isDisabled) {
        const currentValue = formValues[fieldId];
        const hasChanged = currentValue !== value;
        if (hasChanged) {
          const newValues = { ...formValues, [fieldId]: value };
          setFormValues?.(newValues);
        }
      }
    },
    [isDisabled, formValues]
  );

  const getFieldGridClasses = useCallback(
    (fieldId: number) => {
      if (gridPositions && gridPositions[fieldId as keyof typeof gridPositions]) {
        const position = gridPositions[fieldId as keyof typeof gridPositions];
        if (position) {
          const { row, col, span } = position;
          const gridClasses = [`rowStart${row}`, `colStart${col}`, `span${span}`];
          return gridClasses.join(' ');
        }
      }
      return 'span2';
    },
    [gridPositions]
  );

  const getFieldDisplayValue = useCallback(
    (fieldId: number): string => {
      const value = formValues[fieldId];
      if (value instanceof File) {
        return value.name;
      }
      return typeof value === 'string' ? value : '';
    },
    [formValues]
  );

  const createFieldChangeHandler = useCallback(
    (fieldId: number) => (value: string | File) => handleChange(fieldId, value),
    [handleChange]
  );

  const createTextAreaChangeHandler = useCallback(
    (fieldId: number) => (value: string | ((prevState: string) => string)) => {
      const stringValue = typeof value === 'function' ? value('') : value;
      handleChange(fieldId, stringValue);
    },
    [handleChange]
  );

  const createFileChangeHandler = useCallback(
    (fieldId: number) => (file?: File) => handleChange(fieldId, file || ''),
    [handleChange]
  );

  useEffect(() => {
    if (formValues && JSON.stringify(formValues) !== JSON.stringify(lastInitialValues.current)) {
      setFormValues?.(formValues);
      lastInitialValues.current = formValues;
    }
  }, [formValues]);

  useEffect(() => {
    if (!isInitialMount.current) {
      setFormValues?.(formValues);
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [formValues, setFormValues]);

  useEffect(() => {
    setIsFormValid?.(validateRequiredFields(formValues));
  }, [validateRequiredFields, setIsFormValid, formValues]);

  return (
    <form className="configFieldsGrid">
      {fields?.map(field => {
        const gridClasses = getFieldGridClasses(field.id);
        const fieldValue = getFieldDisplayValue(field.id);
        const fieldLabel = field.isRequired ? field.label : `${field.label} (Opcional)`;

        let fieldComponent;
        switch (field.fieldType) {
          case CONFIG_FIELD_TYPE.TEXT:
          case CONFIG_FIELD_TYPE.TEL:
          case CONFIG_FIELD_TYPE.NUMBER:
          case CONFIG_FIELD_TYPE.EMAIL:
          case CONFIG_FIELD_TYPE.PASSWORD: {
            fieldComponent = (
              <NudosTextInput
                key={field.id}
                label={fieldLabel}
                placeholder={field.placeholder}
                defaultValue={fieldValue}
                handleChange={createFieldChangeHandler(field.id)}
                isDeactivated={isDisabled}
                isFilled={!isDisabled}
              />
            );
            break;
          }
          case CONFIG_FIELD_TYPE.TEXTAREA: {
            fieldComponent = (
              <div key={field.id}>
                <NudosTextArea
                  label={fieldLabel}
                  placeholder={field.placeholder}
                  currentValue={fieldValue}
                  setCurrentValue={createTextAreaChangeHandler(field.id)}
                  isDeactivated={isDisabled}
                  isFilled={!isDisabled}
                  textHeight={64}
                  hidenCounter
                  maxLength={5000}
                />
              </div>
            );
            break;
          }
          case CONFIG_FIELD_TYPE.FILE: {
            fieldComponent = (
              <NudosUploadDownloadButton
                key={field.id}
                customClassName="fileUploadContainer"
                uploadedFileHandler={createFileChangeHandler(field.id)}
                currentFileUrl={fieldValue || undefined}
                buttonEmptyText={field.placeholder}
                buttonFilledText={isDisabled ? field.placeholder : fieldValue}
                downloadButtonText="Descargar archivo"
                isDisabled={isDisabled}
                fakeLabel={field.fakeLabel ?? true}
                customWidth="346px"
                customHeight="38px"
                resetSelectionWhenClickingWhenFilled={false}
              />
            );
            break;
          }
          default:
            fieldComponent = null;
        }

        return (
          <div key={field.id} className={`configFieldItem ${gridClasses}`}>
            {fieldComponent}
          </div>
        );
      })}
      {showAlert && alert && (
        <NudosStaticAlertInComponent
          customClassname="configFieldAlert"
          type={alert.type}
          alertOptions={{
            title: alert.title
          }}
        />
      )}
    </form>
  );
};

export default ConfigFieldsForm;
